import store, { bfiUserActions, userActions } from '@apply/adminStore';
import { jwtDecode } from 'jwt-decode';
import { BfiApi, IApiResponse } from './bfiApi';

export interface ILoginCredentials {
    email: string;
    password: string;
}

export interface ILoginInformation extends ILoginCredentials {
    ipaddress: string;
}

export class LoginApi extends BfiApi {
    async login(credentials: ILoginInformation) {
        const { dispatch } = store;
        const response = await super.post<Promise<IApiResponse>>('/account/admin/login', credentials);

        if (response.statusCode !== 200 || !response.result.value.token) {
            return response;
        }

        const data = response.result.value;
        const decoded: any = jwtDecode(data.token);
        const _user = {
            success: true,
            authenticated: true,
            admin: data.user.isAdmin,
            userId: data.user.userId,
            companies: data.companies,
            broker: data.broker,
            isBroker: data.user.isBroker,
            brokerId: data.user.brokerId,
            ffinuser: data.token,
            refreshToken: data.refreshToken,
            expires: decoded.exp * 1000,
            firstName: data.user.firstName,
            lastName: data.user.lastName,
            emailAddress: data.user.emailAddress,
            requiresESign: data.requiresESign,
            requiresAdminTermsESign: data.requiresAdminTermsESign,
            statusCode: response.statusCode,
            featureList: data.featureList,
            roles: data.roles,
            areTermsAccepted: data.user.areTermsAccepted,
        };

        const loginDT = new Date().getTime();
        dispatch(userActions.userUpdate(_user, loginDT) as never);
        dispatch(bfiUserActions.bfiUserUpdate(_user, loginDT) as never);

        return _user;
    }

    getIpAddress = async (): Promise<IApiResponse> => {
        const requestOptions: RequestInit = {
            method: 'GET',
        };

        const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace', requestOptions);
        if (response.ok) {
            const resultText = await response.text();
            const resultJson = (resultText ?? '')
                .trim()
                .split('\n')
                .reduce(function (obj, pair) {
                    const tokens = pair.split('=');
                    return ((obj[tokens[0]] = tokens[1]), obj);
                }, {});

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const ip = (resultJson as any).ip;
            if (!ip) {
                this.logError('Failed to retrieve IP address! External service did not return IP.');
                return {
                    statusCode: response.status,
                    errors: [],
                    result: '',
                };
            }

            return {
                statusCode: response.status,
                errors: [],
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                result: ip,
            };
        } else {
            this.logError('Failed to retrieve IP address! External service did not return IP.');
            return {
                statusCode: response.status,
                errors: ['failed to retrieve user ip address'],
                result: '',
            };
        }
    };

    getCookie(name: string) {
        return store.getState().user[name];
    }

    async logout() {
        return await super
            .post<Promise<IApiResponse>>('/account/logoff')
            .catch((err) => err.response)
            .finally(() => {
                window.localStorage.removeItem('ffinuser');
                window.localStorage.removeItem('bfiRefreshToken');
                window.localStorage.removeItem('bfiUser');
                window.localStorage.removeItem('bfi_state');
                userActions.userLogOut();
            });
    }

    isAuthenticated() {
        const cookie = this.getCookie('ffinuser');
        if (cookie != null) {
            if (this.confirmStorageExpiration('ffinuser')) {
                window.localStorage.removeItem('ffinuser');
                window.localStorage.removeItem('bfiRefreshToken');
                window.localStorage.removeItem('bfiUser');
                window.localStorage.removeItem('bfi_state');
                userActions.userLogOut();
                window.location.href = '/';
                return false;
            } else {
                return cookie && cookie !== '""';
            }
        } else {
            return false;
        }
    }

    confirmStorageExpiration(name) {
        const userStoredItem = window.localStorage.getItem(name);
        if (userStoredItem != null) {
            const localStorageItem = JSON.parse(userStoredItem);
            const tsDate = new Date(localStorageItem.timestamp);
            const nowDate = new Date();
            //Force 12 hour clear of the token from the user
            tsDate.setTime(tsDate.getTime() + 12 * 60 * 60 * 1000);
            if (nowDate > tsDate) {
                return true;
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    async forgotPassword(obj) {
        return super.post<Promise<IApiResponse>>('/account/forgotPassword', obj);
    }

    async forgotPasswordById(principalId: number) {
        return super.post<Promise<IApiResponse>>(`/account/forgotPasswordbyid/${principalId}`);
    }

    async forgotPasswordByUserId(principalId: string) {
        return super.post<Promise<IApiResponse>>(`/account/forgotPasswordByUserId/${principalId}`);
    }

    async forgotPasswordGeneralContractorId(generalContractorId: number) {
        return super.post<Promise<IApiResponse>>(`/account/forgotPasswordGeneralContractorId/${generalContractorId}`);
    }

    async getUserStatus(obj) {
        return super.post('/account/getUserStatus', obj);
    }

    async resetPasswordPreCheck(obj) {
        return super.post<Promise<IApiResponse>>('/account/resetPassword/preCheck', obj);
    }

    async resetPassword(obj) {
        return super.post<Promise<IApiResponse>>('/account/resetPassword', obj);
    }

    async updatePassword(obj) {
        return super.post<Promise<IApiResponse>>('/account/updatePassword', {
            ...obj,
            userId: this.getCookie('userId'),
        });
    }
}
